/* eslint-disable react/no-danger -- when updating this component, consider replacing dangerous jsx properties */
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import React, { useEffect, memo } from 'react';

import { token } from '@atlaskit/tokens';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { loadResources } from '@confluence/wrm/entry-points/loadResources.ts';
import { requireSuperBatch } from '@confluence/wrm/entry-points/requireSuperBatchHelpers.ts';
import { evalLegacyConnectInlineScripts } from '@confluence/connect-utils';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';

import type { CustomHeaderFooterQuery$data } from './__generated__/CustomHeaderFooterQuery.graphql.ts';
import { CUSTOM_HEADER_FOOTER_TYPES } from './constants.ts';
import {
	StyledRoot,
	StyledUnsupportedMacro,
	StyledHeader,
	StyledFooter,
} from './PresentationalComponents.tsx';

type SpaUnfriendlyMacroType = NonNullable<
	NonNullable<
		NonNullable<
			NonNullable<CustomHeaderFooterQuery$data['space']>['settings']
		>['customHeaderAndFooter']['footer']
	>['spaUnfriendlyMacros']
>[number];

type CustomHeaderFooterComponentProps = {
	type: 'header' | 'footer';
	spaUnfriendlyMacros: SpaUnfriendlyMacroType[];
	js: string[];
	css: string;
	html: string;
};

const i18n = defineMessages({
	unsupportedMacrosTitle: {
		id: 'custom-header-footer.unsupported.macros.title',
		defaultMessage: 'Unsupported macros',
		description: 'Space cutomized header and footer is imcompitable with unsupported macros',
	},
});

export const CustomHeaderFooterComponent = memo(
	({ spaUnfriendlyMacros = [], type, js, css, html }: CustomHeaderFooterComponentProps) => {
		const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
		const intl = useIntl();
		const HeaderOrFooterComponent =
			type === CUSTOM_HEADER_FOOTER_TYPES.HEADER ? StyledHeader : StyledFooter;
		const bodyRef = React.createRef<HTMLDivElement>();
		const scriptRef = React.createRef<HTMLDivElement>();
		// remove duplicates
		spaUnfriendlyMacros = Array.from(new Set(spaUnfriendlyMacros));

		const { createAnalyticsEvent } = useAnalyticsEvents();

		useEffect(() => {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'customHeaderAndFooter',
					attributes: {
						type: type === CUSTOM_HEADER_FOOTER_TYPES.HEADER ? 'header' : 'footer',
					},
				},
			}).fire();
		}, [createAnalyticsEvent, type]);

		useEffect(() => {
			if (!scriptRef.current || !bodyRef.current) {
				return;
			}
			void requireSuperBatch().then(() => {
				if (scriptRef.current) {
					void loadResources(scriptRef.current, js).then(() => {
						if (bodyRef.current) {
							void evalLegacyConnectInlineScripts(bodyRef.current);
						}
					});
				}
			});
		});

		return (
			<StyledRoot data-vc="custom-header-footer" {...ssrPlaceholderIdProp}>
				{spaUnfriendlyMacros.length ? (
					<StyledUnsupportedMacro>
						<StatusWarningIcon
							color={token('color.icon.warning')}
							label={intl.formatMessage(i18n.unsupportedMacrosTitle)}
						/>
						<FormattedMessage
							id="custom-header-footer.unsupported.macros.list"
							defaultMessage="The following macros are not currently supported in the {type}:"
							description="The warning message when a custom space header or footer contain incompatible macros. type variable equals to 'header' or 'footer'"
							values={{ type }}
						/>
						<ul>
							{spaUnfriendlyMacros.map(
								(macro) => macro && macro.name && <li key={macro.name}>{macro.name}</li>,
							)}
						</ul>
					</StyledUnsupportedMacro>
				) : null}
				<div dangerouslySetInnerHTML={{ __html: css }} />
				<HeaderOrFooterComponent
					data-test-id={`custom-settings-${type}`}
					dangerouslySetInnerHTML={{ __html: html }}
					ref={bodyRef}
				/>
				<div ref={scriptRef} />
			</StyledRoot>
		);
	},
);
